import React, { useCallback, useEffect, useMemo, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash-es';
import { InventoryEntities } from 'client/data/models/inventory';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildVinPhotosPath, PhotoflipperModel, VinPhotosEntity } from 'client/data/models/photoflipper';
import { TrackingConstant } from 'client/tracking/constant';
import { DATA_PATH } from 'client/engagement-handlers/inventory-engagement-handler/constants';
import { trackShowSilhouette } from 'site-modules/shared/utils/inventory/tracking';
import { getInventoryHeroPhotoConfig } from 'site-modules/inventory/utils/get-inventory-hero-photo-config';
import { LARGE_VDP_WIDTH } from 'site-modules/shared/utils/inventory-image-utils';
import { With360Photo } from 'site-modules/inventory/components/inventory-hero/with-360-photo/with-360-photo';
import { WithPhotoflipper } from 'site-modules/inventory/components/inventory-hero/with-photfliper/with-photofliper';
import { InventoryHeroImage } from 'site-modules/inventory/components/inventory-hero/inventory-hero-image/inventory-hero-image';

const InventoryHeroUI = memo(
  ({
    vinData = {},
    isCpo = false,
    isMobile = false,
    disableInventoryMLPhotoSort = false,
    photo360 = null,
    showMultipleImages = false,
    showSliderArrows = false,
    vinPhotos = null,
    hideLeadForm = false,
    showCapOneEntryPoint = false,
  }) => {
    const carouselRef = useRef();
    const isMissingPhotoTracked = useRef(false);

    const dealerPhotoCount = get(vinData, DATA_PATH.VDP_DEALER_PHOTO, 0);
    const stockPhotoUrl = get(vinData, DATA_PATH.STOCK_PHOTO_URL);
    const isStockPhotos = !dealerPhotoCount && !!stockPhotoUrl;

    const { make, model, year, trim } = get(vinData, 'vehicleInfo.styleInfo', {});
    const vin = get(vinData, 'vin', null);
    const stockNum = get(vinData, 'stockNumber', '');

    const trackMissingPhoto = useCallback(() => {
      const hasDealerPhotos = !!dealerPhotoCount;
      if (isMissingPhotoTracked.current || isEmpty(vinData) || !!stockPhotoUrl || hasDealerPhotos) {
        return;
      }

      isMissingPhotoTracked.current = true;
      trackShowSilhouette(TrackingConstant.SHOW_SILHOUETTE_MISSING, get(vinData, 'vin'));
    }, [dealerPhotoCount, stockPhotoUrl, vinData]);

    useEffect(() => {
      trackMissingPhoto();
    }, [trackMissingPhoto]);

    const handleClosed = useCallback(() => {
      carouselRef?.current?.goto(0);
    }, []);

    const {
      photoUrlsWithMetadata: photoUrls,
      hasPhotos,
      photoType,
      thumbnailPhotoUrls,
    } = useMemo(
      () =>
        getInventoryHeroPhotoConfig({
          vinData,
          width: LARGE_VDP_WIDTH,
          disableInventoryMLPhotoSort,
          isMobile,
          photosMetadata: vinPhotos,
        }),
      [disableInventoryMLPhotoSort, isMobile, vinData, vinPhotos]
    );

    return (
      <>
        <With360Photo photo360={photo360} stockNum={stockNum} photoUrls={photoUrls}>
          {(hasPhoto360, photoInfo, photoUrlsFull, carmaxFallback) => (
            <WithPhotoflipper
              make={make}
              model={model}
              year={year}
              trim={trim}
              isCpo={isCpo}
              vin={vin}
              stockNum={stockNum}
              onClosed={handleClosed}
              carmaxFallback={carmaxFallback}
              photoUrls={photoUrlsFull}
              photoInfo={photoInfo}
              hasPhoto360={hasPhoto360}
              showCapOneEntryPoint={showCapOneEntryPoint}
              hideLeadForm={hideLeadForm}
              vinData={vinData}
            >
              {(toggleModal, setCurrentSlideIndex) => (
                <InventoryHeroImage
                  vinData={vinData}
                  isMobile={isMobile}
                  showMultipleImages={showMultipleImages}
                  showSliderArrows={showSliderArrows}
                  toggleModal={toggleModal}
                  onIndexChange={setCurrentSlideIndex}
                  isStockPhotos={isStockPhotos}
                  hasPhoto360={hasPhoto360}
                  photoUrls={photoUrls}
                  hasPhotos={hasPhotos}
                  thumbnailPhotoUrls={thumbnailPhotoUrls}
                  photoType={photoType}
                  forwardedRef={carouselRef}
                />
              )}
            </WithPhotoflipper>
          )}
        </With360Photo>
      </>
    );
  }
);

InventoryHeroUI.propTypes = {
  vinData: InventoryEntities.InventoryVin,
  isCpo: PropTypes.bool,
  isMobile: PropTypes.bool,
  disableInventoryMLPhotoSort: PropTypes.bool,
  photo360: PropTypes.shape({ provider: PropTypes.string, hasExterior: PropTypes.bool, hasInterior: PropTypes.bool }),
  showMultipleImages: PropTypes.bool,
  showSliderArrows: PropTypes.bool,
  vinPhotos: VinPhotosEntity,
  hideLeadForm: PropTypes.bool,
  showCapOneEntryPoint: PropTypes.bool,
};

InventoryHeroUI.displayName = 'InventoryHeroUI';

export const stateToPropsConfig = {
  vinPhotos: bindToPath(({ vinData: { vin } }) => buildVinPhotosPath({ vin }), PhotoflipperModel, null, false),
};

export const InventoryHero = connectToModel(InventoryHeroUI, stateToPropsConfig);
